import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import StopIcon from '@material-ui/icons/Stop';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { getAiScriberStatus, getAiScriberSessionId, getAiScriberElapsedSeconds, getAiScriberTranscript, getAiScriberErrorKey } from '../../store/aiScriber/selectors';
import { selectAiScriberTemplate, aiScriberStopped, transcriptDeltaReceived, aiScriberElapsedTick, resetAiScriber, reportDraftReceived } from '../../store/aiScriber/actions';
import { ReportTemplateData } from '../../models';
import { ComboField } from '../Forms/fields';
import AppButton from '../AppButton';
import Typography from '../Typography';
import { websocketService } from '../../services/websocketService';
import { postAiScriberAudioChunkAsync, stopAiScriberSessionAsync } from '../../ajax/requests';

interface AiScriberProps {
    appointmentId: string;
    templates: ReportTemplateData[];
}

const Container = styled.div`
    margin-top: var(--spacer-S);
    padding: var(--spacer-S);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 4px;
    background-color: var(--bg-light);
`;

const RecordingIndicator = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    color: var(--danger-color);
    font-weight: 500;
`;

const PulseDot = styled.span<{ active: boolean }>`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: var(--danger-color);
    display: inline-block;
    animation: ${({ active }) => active ? 'pulse 1.2s ease-in-out infinite' : 'none'};

    @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.2; }
    }
`;

const TranscriptBox = styled.div`
    margin-top: var(--spacer-S);
    padding: var(--spacer-S);
    background-color: var(--bg-light);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 4px;
    min-height: 80px;
    max-height: 200px;
    overflow-y: auto;
    white-space: pre-wrap;
    font-size: 0.9em;
    color: var(--text-body);
`;

const Row = styled.div<React.HTMLAttributes<HTMLDivElement>>`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
    flex-wrap: wrap;
`;

const formatElapsed = (seconds: number): string => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

const AiScriber: React.FC<AiScriberProps> = ({ appointmentId, templates }) => {
    const t = useTranslations();
    const dispatch = useDispatch();

    const status = useSelector(getAiScriberStatus);
    const sessionId = useSelector(getAiScriberSessionId);
    const elapsedSeconds = useSelector(getAiScriberElapsedSeconds);
    const transcript = useSelector(getAiScriberTranscript);
    const errorKey = useSelector(getAiScriberErrorKey);

    const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(undefined);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const timerRef = useRef<number | null>(null);

    const isThisAppointmentActive = useSelector((state: any) =>
        state.aiScriber.appointmentId === appointmentId &&
        status !== 'idle'
    );

    const needsSignalRWiring = isThisAppointmentActive && status !== 'draftReady';

    // Wire up SignalR callbacks while this component is mounted during an active session
    useEffect(() => {
        if (!needsSignalRWiring) return;

        websocketService.onAiScriberTranscriptDelta((_sid, delta) => {
            dispatch(transcriptDeltaReceived(delta));
        });

        websocketService.onAiScriberReportDraftGenerated((_sid, draft, templateId) => {
            dispatch(reportDraftReceived(draft, templateId));
        });

        return () => {
            websocketService.onAiScriberTranscriptDelta(() => {});
            websocketService.onAiScriberReportDraftGenerated(() => {});
        };
    }, [needsSignalRWiring, dispatch]);

    // Start MediaRecorder once session ID is available and status is 'recording'
    useEffect(() => {
        if (status !== 'recording' || !sessionId) return;

        let active = true;

        const startRecording = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                if (!active) {
                    stream.getTracks().forEach(t => t.stop());
                    return;
                }
                streamRef.current = stream;

                const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                    ? 'audio/webm;codecs=opus'
                    : 'audio/webm';

                const recorder = new MediaRecorder(stream, { mimeType });
                mediaRecorderRef.current = recorder;

                recorder.ondataavailable = (e) => {
                    if (e.data.size > 0 && sessionId) {
                        postAiScriberAudioChunkAsync(appointmentId, sessionId, e.data).catch(err => {
                            console.error('Failed to send audio chunk:', err);
                        });
                    }
                };

                recorder.start(1000); // emit chunks every 1 second

                timerRef.current = window.setInterval(() => {
                    dispatch(aiScriberElapsedTick());
                }, 1000);
            } catch (err) {
                console.error('Failed to start MediaRecorder:', err);
            }
        };

        startRecording();

        return () => {
            active = false;
        };
    }, [status, sessionId, dispatch]);

    // Stop MediaRecorder when status transitions out of 'recording'
    useEffect(() => {
        if (status !== 'stopping') return;

        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current = null;
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }

        if (sessionId) {
            stopAiScriberSessionAsync(appointmentId, sessionId).catch(err => {
                console.error('Failed to stop AI scriber session:', err);
            });
        }
    }, [status, sessionId]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
                mediaRecorderRef.current.stop();
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(t => t.stop());
            }
        };
    }, []);

    const handleStart = useCallback(() => {
        if (!selectedTemplateId) return;
        dispatch(selectAiScriberTemplate(appointmentId, selectedTemplateId));
    }, [dispatch, appointmentId, selectedTemplateId]);

    const handleStop = useCallback(() => {
        dispatch(aiScriberStopped());
    }, [dispatch]);

    const handleClose = useCallback(() => {
        dispatch(resetAiScriber());
        setSelectedTemplateId(undefined);
    }, [dispatch]);

    const templateOptions = templates
        .filter(tpl => !tpl.disabled)
        .map(tpl => ({ value: tpl.templateId, label: tpl.name }));

    if (status === 'idle' || !isThisAppointmentActive) {
        return null;
    }

    return (
        <Container>
            {status === 'selectingReportTemplate' && (
                <>
                    <Typography style={{ marginBottom: 'var(--spacer-XS)', fontWeight: 500 }}>
                        {t(TK.aiScriber)}
                    </Typography>
                    <ComboField
                        label={t(TK.aiScriberSelectTemplate)}
                        value={selectedTemplateId}
                        options={templateOptions}
                        onChange={setSelectedTemplateId}
                    />
                    <Row style={{ marginTop: 'var(--spacer-S)' }}>
                        <AppButton color="primary" size="sm" onClick={handleStart} disabled={!selectedTemplateId}>
                            {t(TK.aiScriberStart)}
                        </AppButton>
                        <AppButton color="link" size="sm" onClick={handleClose}>
                            {t(TK.aiScriberClose)}
                        </AppButton>
                    </Row>
                </>
            )}

            {status === 'requestingMicrophonePermission' && (
                <Typography style={{ color: 'var(--text-muted)' }}>
                    {t(TK.aiScriberRequestingMicrophone)}
                </Typography>
            )}

            {(status === 'recording' || status === 'stopping') && (
                <>
                    <Row>
                        <RecordingIndicator>
                            <PulseDot active={status === 'recording'} />
                            <span>{t(TK.aiScriberRecording)} — {formatElapsed(elapsedSeconds)}</span>
                        </RecordingIndicator>
                        {status === 'recording' && (
                            <AppButton color="danger" size="sm" onClick={handleStop}>
                                <StopIcon style={{ fontSize: '1rem' }} />
                                {t(TK.aiScriberStopRecording)}
                            </AppButton>
                        )}
                        {status === 'stopping' && (
                            <Typography size="small" style={{ color: 'var(--text-muted)' }}>
                                {t(TK.aiScriberStopping)}
                            </Typography>
                        )}
                    </Row>
                    {transcript && (
                        <>
                            <Typography size="small" style={{ marginTop: 'var(--spacer-S)', color: 'var(--text-muted)' }}>
                                {t(TK.aiScriberTranscriptLabel)}
                            </Typography>
                            <TranscriptBox>{transcript}</TranscriptBox>
                        </>
                    )}
                </>
            )}

            {status === 'transcriptReady' && (
                <>
                    <Typography style={{ fontWeight: 500, color: 'var(--success-color)', marginBottom: 'var(--spacer-XS)' }}>
                        {t(TK.aiScriberTranscriptReady)}
                    </Typography>
                    <Typography size="small" style={{ color: 'var(--text-muted)', marginBottom: 'var(--spacer-XS)' }}>
                        {t(TK.aiScriberTranscriptLabel)}
                    </Typography>
                    <TranscriptBox>{transcript}</TranscriptBox>
                    <Typography size="small" style={{ color: 'var(--text-muted)', marginTop: 'var(--spacer-XS)' }}>
                        {t(TK.aiScriberProcessing)}
                    </Typography>
                    <div style={{ marginTop: 'var(--spacer-S)' }}>
                        <AppButton color="link" size="sm" onClick={handleClose}>
                            {t(TK.aiScriberClose)}
                        </AppButton>
                    </div>
                </>
            )}

            {status === 'draftReady' && (
                <>
                    <Typography style={{ fontWeight: 500, color: 'var(--success-color)', marginBottom: 'var(--spacer-XS)' }}>
                        {t(TK.aiScriberDraftReady)}
                    </Typography>
                    <Typography size="small" style={{ color: 'var(--text-muted)', marginBottom: 'var(--spacer-S)' }}>
                        {t(TK.aiScriberDraftReadyHint)}
                    </Typography>
                    <div style={{ marginTop: 'var(--spacer-XS)' }}>
                        <AppButton color="link" size="sm" onClick={handleClose}>
                            {t(TK.aiScriberClose)}
                        </AppButton>
                    </div>
                </>
            )}

            {status === 'error' && (
                <>
                    <Typography style={{ color: 'var(--danger-color)', marginBottom: 'var(--spacer-XS)' }}>
                        {errorKey ? t(errorKey as any) : t(TK.aiScriberStartError)}
                    </Typography>
                    <AppButton color="link" size="sm" onClick={handleClose}>
                        {t(TK.aiScriberClose)}
                    </AppButton>
                </>
            )}
        </Container>
    );
};

export default AiScriber;
